import { Injectable, OnDestroy } from '@angular/core';
import { DxDataGridComponent } from 'devextreme-angular/ui/data-grid';
import { DxiDataGridColumn } from 'devextreme-angular/ui/nested/base/data-grid-column-dxi';
import ArrayStore from 'devextreme/data/array_store';
import CustomStore from 'devextreme/data/custom_store';
import DataSource, { DataSourceOptions } from 'devextreme/data/data_source';
import notify from 'devextreme/ui/notify';
import { compact, isNil, uniq, uniqBy } from 'lodash-es';
import isEmpty from 'lodash-es/isEmpty';
import sortBy from 'lodash-es/sortBy';
import { from, Observable, of, OperatorFunction, pipe } from 'rxjs';
import { catchError, combineAll, finalize, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { oc } from 'ts-optchain';
import { DxCellPreparedEvent } from '../../../core/dx.types';
import { LoopBackStoreOptions } from '../../../shared/classes/loopback-custom-store/generic/store-options/LoopBackStoreOptions';
import { gqlMongoLoad } from '../../../shared/classes/loopback-custom-store/generic/store.utils';
import { hAll, hTenant } from '../../../shared/classes/utils/utils';
import { DataSourceService } from '../../../shared/modules/my-common/services/datasource.service';
import {
  Consumer,
  Employee,
  Facility,
  SignatureApi,
  SignatureInterface,
  SignatureView,
  Vehicle,
} from '../../../shared/sdk';
import { SVObject, TDsItem } from '../components/service-validation/types';

@Injectable() // Не 'root', так как провайдим в компоненте
export class ValidationService implements OnDestroy {
  private config: { grid: DxDataGridComponent };
  // private config$ = new ReplaySubject<{ grid: DxDataGridComponent }>(1);

  constructor(
    private dss: DataSourceService,
    private sapi: SignatureApi,
  ) {}

  public init(localArg: { grid: DxDataGridComponent }): void {
    this.config = localArg;
  }

  buildEmptyDataSource() {
    return new DataSource({ store: new ArrayStore({ data: [], key: 'id' }), key: 'id' });
  }

  buildFacilityDataSource() {
    const so = this.dss.getStoreOptions(Facility, undefined, false) as LoopBackStoreOptions<any, any>;
    so.customFilter = {
      where: {
        status: 'ACTIVE',
        type: { inq: ['BASE', 'ADC', 'MEALS'] },
        shortname: { nin: ['N/A', 'MONT'] }, // TODO: remove hardcode
      },
      order: ['typeOrder DESC', 'type', 'shortname'],
    };

    return {
      store: new CustomStore(so),
    } as DataSourceOptions;
  }

  buildConsumerDataSource(facilityId: number) {
    const so = this.dss.getStoreOptions(Consumer, undefined, false) as LoopBackStoreOptions<any, any>;
    so.customHeaders = hTenant(oc(facilityId)(-1));

    return {
      store: new CustomStore(so),
    } as DataSourceOptions;
  }

  buildEmployeeDataSource(facilityId: number) {
    const so = this.dss.getStoreOptions(Employee, undefined, false) as LoopBackStoreOptions<any, any>;
    so.customHeaders = hTenant(oc(facilityId)(-1));

    return {
      store: new CustomStore(so),
    } as DataSourceOptions;
  }

  buildVehicleDataSource() {
    const so = this.dss.getStoreOptions(Vehicle, undefined, false) as LoopBackStoreOptions<any, any>;
    // so.customHeaders = hTenant(oc(facilityId)(-1));;
    so.customHeaders = hAll;

    return {
      store: new CustomStore(so),
    } as DataSourceOptions;
  }

  buildConsumerSubDataSource$() {
    return of(true).pipe(
      switchMap(
        () =>
          this.gridStore.load({
            select: ['consumerId', 'consumer_person_firstname', 'consumer_person_lastname'] as (keyof SignatureView)[],
          }) as Promise<(SignatureView & { name? })[]>,
      ),
      map(docs => uniqBy(docs, d => d.consumerId).filter(d => d.consumerId)),
      map(docs => {
        docs.forEach(doc => {
          doc.name = doc.consumer_person_firstname + ' ' + doc.consumer_person_lastname;
        });
        docs = sortBy(docs, 'name');

        const dso: DataSourceOptions = {
          store: new ArrayStore({ data: docs, key: 'consumerId' }),
        };
        return dso;
      }),
    );
  }

  _buildConsumerSubDataSource$(dataCollection: string) {
    return gqlMongoLoad<SignatureView>(this.dss, dataCollection, {
      select: { consumerId: true, consumer_person_firstname: true, consumer_person_lastname: true } as {
        [P in keyof SignatureView]: boolean;
      },
    }).pipe(
      map(docs => uniqBy(docs, 'consumerId').filter(d => d.consumerId)),
      map(docs => {
        docs.forEach(doc => {
          (doc as any).name = doc.consumer_person_firstname + ' ' + doc.consumer_person_lastname;
        });
        docs = sortBy(docs, 'name');

        // const so = this.dss.getStoreOptions(ConsumerView, null, false) as LoopBackStoreOptions<any, any>;
        // so.customHeaders = headersAllTenantsAppend;
        const dso: DataSourceOptions = {
          store: new ArrayStore({ data: docs, key: 'consumerId' }),
          // store: new CustomStore(so),
          // filter: [['id', 'inq', compact(uniqBy(docs, 'consumerId').map(r => r.consumerId))]],
        } as DataSourceOptions;
        return dso;
      }),
    );
  }

  buildEmployeeSubDataSource$() {
    return of(true).pipe(
      switchMap(
        () =>
          this.gridStore.load({
            select: ['employeeId', 'employee_person_firstname', 'employee_person_lastname'] as (keyof SignatureView)[],
          }) as Promise<(SignatureView & { name? })[]>,
      ),
      map(docs => uniqBy(docs, d => d.employeeId).filter(d => d.employeeId)),
      map(docs => {
        docs.forEach(doc => {
          doc.name = doc.employee_person_firstname + ' ' + doc.employee_person_lastname;
        });
        docs = sortBy(docs, 'name');

        const dso: DataSourceOptions = {
          store: new ArrayStore({ data: docs, key: 'employeeId' }),
        };
        return dso;
      }),
    );
  }

  _buildEmployeeSubDataSource$(dataCollection: string) {
    return gqlMongoLoad<SignatureView>(this.dss, dataCollection, {
      select: { employeeId: true, employee_person_firstname: true, employee_person_lastname: true } as {
        [P in keyof SignatureView]: boolean;
      },
    }).pipe(
      map(docs => uniqBy(docs, 'employeeId').filter(d => d.employeeId)),
      map(docs => {
        docs.forEach(doc => {
          (doc as any).name = doc.employee_person_firstname + ' ' + doc.employee_person_lastname;
        });
        docs = sortBy(docs, 'name');

        // const so = this.dss.getStoreOptions(EmployeeView, null, false) as LoopBackStoreOptions<any, any>;
        // so.customHeaders = headersAllTenantsAppend;
        const dso: DataSourceOptions = {
          store: new ArrayStore({ data: docs, key: 'employeeId' }),
          // store: new CustomStore(so),
          // filter: [['id', 'inq', compact(uniqBy(docs, 'employeeId').map(r => r.employeeId))]],
        } as DataSourceOptions;
        return dso;
      }),
    );
  }

  buildVehicleSubDataSource$() {
    return of(true).pipe(
      switchMap(
        () =>
          this.gridStore.load({
            select: ['vehicleId', 'vehicle_internalId'] as (keyof SignatureView)[],
          }) as Promise<SignatureView[]>,
      ),
      map(docs => uniqBy(docs, d => d.vehicleId).filter(d => d.vehicleId)),
      map(docs => {
        docs = sortBy(docs, 'vehicle_internalId');

        const dso: DataSourceOptions = {
          store: new ArrayStore({ data: docs, key: 'vehicleId' }),
        };
        return dso;
      }),
    );
  }

  _buildVehicleSubDataSource$(dataCollection: string) {
    return gqlMongoLoad<SignatureView>(this.dss, dataCollection, {
      select: { vehicleId: true, vehicle_internalId: true } as {
        [P in keyof SignatureView]: boolean;
      },
    }).pipe(
      map(docs => uniqBy(docs, 'vehicleId').filter(d => d.vehicleId)),
      map(docs => {
        docs = sortBy(docs, 'vehicle_internalId');

        // const so = this.dss.getStoreOptions(Vehicle, null, false) as LoopBackStoreOptions<any, any>;
        // so.customHeaders = headersAllTenantsAppend;
        const dso: DataSourceOptions = {
          store: new ArrayStore({ data: docs, key: 'vehicleId' }),
          // store: new CustomStore(so),
          // filter: [['id', 'inq', compact(uniqBy(docs, 'vehicleId').map(r => r.vehicleId))]],
        } as DataSourceOptions;
        return dso;
      }),
    );
  }

  batchSignatureUpdate$(
    iteratee: (doc: SignatureView) => boolean,
    data: Partial<SignatureInterface>,
    updateFn: (items: { key: number; values: Partial<SignatureInterface> }[]) => Promise<any> | Observable<any>,
  ) {
    return of(true).pipe(
      tap(() => {
        this.config.grid.instance.beginCustomLoading('Updating...');
        this.config.grid.instance.beginUpdate();
      }),
      switchMap(() =>
        from(
          (async () => {
            const selected: SignatureView[] = await this.config.grid.instance.getSelectedRowsData();
            const visibleRows = this.config.grid.instance.getVisibleRows();
            const visibleIdsSet = new Set(visibleRows.map(r => r.data.id));
            return selected.filter(i => visibleIdsSet.has(i.id)).filter(iteratee);
          })(),
        ).pipe(
          mergeMap(filtered => from(filtered)),
          map(doc =>
            this.sapi.patchAttributes(doc.id, data, hAll).pipe(
              // mergeMap(() => this.svapi.findById<SignatureView>(doc.id, {}, hAll)),
              map(s => ({ key: doc.id, values: s })),
            ),
          ),
          combineAll(),
          mergeMap(items => updateFn(items)),
          finalize(() => {
            this.config.grid.instance.refresh(true);
            this.config.grid.instance.endUpdate();
            this.config.grid.instance.endCustomLoading();
          }),
        ),
      ),

      catchError(err => of(notify(err.message, 'error', 10_000))),
    );
  }

  wrapLoadingCatchOperator<T, O>(op: () => OperatorFunction<T, O>) {
    return pipe(
      tap(() => this.config.grid.instance.beginCustomLoading('Updating...')),
      op(),
      catchError(err => of(notify(err.message, 'error', 10_000))),
      tap(() => this.config.grid.instance.endCustomLoading()),
    );
  }

  get gridStore() {
    return this.config.grid.instance.getDataSource().store() as CustomStore;
  }

  get gridItems() {
    return this.config.grid.instance.getDataSource().items() as TDsItem<SVObject>[];
  }

  storeUpdate$(entries: { key; values }[], itemFn: (key, values) => Promise<void>, withUpdate = false) {
    entries = entries.filter(e => !isEmpty(oc('' + e.key)('')) && !isEmpty(oc(e.values)('')));

    if (entries.length === 0) return of([]);

    return of(true).pipe(
      map(() => this.config.grid.instance.getDataSource()),
      switchMap(async ds => {
        for (const { key, values } of entries) {
          await itemFn(key, values);
        }

        (ds.store() as CustomStore).push(
          entries.map(({ key, values }) => ({
            type: 'update',
            data: values,
            key,
          })),
        );

        if (withUpdate)
          await Promise.all(
            entries.map(({ key, values }) => (ds.store() as CustomStore).update(key, values).catch(console.error)),
          );

        return entries.map(({ key }) => key);
      }),
    );
  }

  processIssueData(doc: SVObject, __issueData: SVObject['__issueData']) {
    const allFields = __issueData
      .map(i => i.field)
      .flat()
      .reduce((o, f) => ({ ...o, [f]: false }), {});

    __issueData = __issueData.filter(i => !isNil(i.issue));

    const __issueFields = {
      ...allFields,
      ...__issueData
        .map(i => i.field)
        .flat()
        .reduce((o, f) => ({ ...o, [f]: true }), {}),
    };

    const __issueList = [...oc(doc.__missingList)([]), ...__issueData.map(i => i.issue)].filter(
      lbl => !isEmpty(oc(lbl)('')),
    );
    const __issueTitle = __issueList.map(itm => '· ' + itm).join('\n');

    const __issueHash = __issueData.length > 0 ? JSON.stringify(__issueData) : null;

    const hashChanged = doc.__issueHash !== __issueHash;
    if (hashChanged) doc.__issueHash = __issueHash;

    doc.__issueData = __issueData;
    doc.__issueFields = __issueFields;
    doc.__issueList = __issueList;
    doc.__issueTitle = __issueTitle;

    // console.log('getIssueData', doc);

    return [
      {
        type: 'update',
        data: {
          ...(hashChanged ? { __issueHash } : {}),
          __issueData,
          __issueFields,
          __issueList,
          __issueTitle,
        },
        key: doc.id,
      },
    ];
  }

  processIssuesForCell(e: DxCellPreparedEvent<SVObject>) {
    const _issuesFn = () => {
      e.cellElement.classList.remove('cell-red');
      e.cellElement.classList.remove('cell-orange');
      e.cellElement.classList.remove('cell-yellow');
      e.cellElement.classList.remove('cell-center');
      // e.cellElement.classList.remove('cell-custom-valid');
      e.cellElement.title = '';

      if (e.column.dataField === '__issueHash') {
        e.cellElement.classList.add('cell-center');
      }

      if (e.data.__issueHash) {
        if (e.column.dataField === '__issueHash') {
          const cellSeverity = new Set(uniq(compact(e.data.__issueData.map(itm => oc(itm.severity)('warning')))));
          e.cellElement.classList.add(
            cellSeverity.has('critical') ? 'cell-red' : cellSeverity.has('warning') ? 'cell-orange' : 'cell-yellow',
          );
          e.cellElement.title = e.data.__issueTitle;
        } else if (this.colInFields(e.data.__issueData.map(itm => itm.field).flat(), e.column)) {
          const __issueData = e.data.__issueData.filter(itm => this.colInFields(itm.field, e.column));
          const cellSeverity = new Set(uniq(compact(__issueData.map(itm => oc(itm.severity)('warning')))));
          e.cellElement.classList.add(
            cellSeverity.has('critical') ? 'cell-red' : cellSeverity.has('warning') ? 'cell-orange' : 'cell-yellow',
          );
          e.cellElement.title = __issueData.map(itm => '· ' + itm.issue).join('\n');
        }
      }
      // else if (oc(e).data._valid()) {
      //   e.cellElement.classList.add('cell-custom-valid');
      //   e.cellElement.title = [
      //     'Client for this day has only 2 trips and 2 tripIDs',
      //     'Trips for this client have proper assignment of flags TO_ADC and FROM_ADC and those alig with PUDO Time',
      //   ]
      //     .map(itm => '· ' + itm)
      //     .join('\n');
      // }
    };

    _issuesFn();
    e.watch(() => e.data.__issueHash, _issuesFn);
  }

  colInFields(fieldsOrCaptions: string[], column: DxiDataGridColumn) {
    return fieldsOrCaptions
      .map(
        v =>
          ({
            consumerId: ['Client Last Name', 'Client First Name', 'MCI'],
            employeeId: ['Employee Last Name', 'Employee First Name'],
            vehicleId: 'Vehicle',
            arrivedTime: 'ARR/PU/DO',
            pickupTime: 'ARR/PU/DO',
            dropoffTime: 'ARR/PU/DO',
          })[v] || v,
      )
      .flat()
      .some(v => [column.dataField, column.caption].includes(v));
  }

  ngOnDestroy() {}
}
